import { FC, useEffect, useMemo, useRef, useState } from 'react'

import { ChevronSmallDown, ChevronSmallUp, Moon24, Reorder16, Sun24 } from '@makyoapp/makyo-icon/icon'

import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { useActiveDevice } from '@/hooks/useActiveDevice'
import { useUpdateCompSettingValue } from '@/hooks/useUpdateCompSettings'
import { BuilderComponentSetting } from '@/shared/builder'
import { BUILDER_DEVICES } from '@/shared/builder-constants'
import { DEFAULT_SECTION_BACKGROUND_GROUP, SectionBackgroundGroupProps } from '@/shared/builder/default/section.default'
import { debounce } from '@/shared/utils'

import {
  ColorBackgroundSelect,
  EffectBackgroundSelect,
  ImageBackgroundSelect,
  MaskBackgroundSelect,
  OverlayBackgroundSelect,
  PatternBackgroundSelect,
  VideoBackgroundSelect,
} from '.'

interface SectionBackgroundSelectProps {
  id: string
  value: SectionBackgroundGroupProps[]
  settingId: string
  compSettings: Record<string, any>
  colorVariantSettings: BuilderComponentSetting['colorVariantSettings']
  imageSelectSettings: BuilderComponentSetting['imageSelectSettings']
  videoSelectSettings: BuilderComponentSetting['videoSelectSettings']
}

export const SectionBackgroundSelect: FC<SectionBackgroundSelectProps> = ({
  id,
  value,
  colorVariantSettings,
  imageSelectSettings,
  videoSelectSettings,
  settingId,
  compSettings,
}) => {
  const [openLayerId, setOpenLayerId] = useState<number | null>(null)

  // Ensure effect layer exists and fix duplicate IDs (migration for old sections)
  const ensureEffectLayer = (layers: SectionBackgroundGroupProps[]): SectionBackgroundGroupProps[] => {
    // First, fix duplicate IDs
    const seenIds = new Set<number>()
    const fixedLayers: SectionBackgroundGroupProps[] = []
    const maxId = Math.max(...layers.map((layer) => layer.id), 0)
    let nextId = maxId + 1

    for (const layer of layers) {
      if (seenIds.has(layer.id)) {
        // Duplicate ID found, assign a new one
        fixedLayers.push({ ...layer, id: nextId++ })
      } else {
        // First occurrence, keep the original ID
        seenIds.add(layer.id)
        fixedLayers.push(layer)
      }
    }

    layers = fixedLayers

    // Check if effect layer exists
    const hasEffectLayer = layers.some((layer) => layer.type === 'effect')
    if (hasEffectLayer) return layers

    // Find the position after overlay (before mask)
    const overlayIndex = layers.findIndex((layer) => layer.type === 'overlay')

    // Find the next available ID for effect layer
    const existingIds = new Set(layers.map((layer) => layer.id))
    let effectLayerId = 7
    while (existingIds.has(effectLayerId)) {
      effectLayerId++
    }

    const effectLayer: SectionBackgroundGroupProps = {
      id: effectLayerId,
      type: 'effect',
      values: {
        effectType: 'none',
        effectSpeed: 100,
        effectOpacity: 100,
        effectColors: ['#6366f1', '#8b5cf6', '#ec4899'],
        effectBackgroundColor: '#000000',
      },
    }

    // Insert effect layer after overlay
    const newLayers = [...layers]
    newLayers.splice(overlayIndex + 1, 0, effectLayer)
    return newLayers
  }

  const [layers, setLayers] = useState<SectionBackgroundGroupProps[]>(
    ensureEffectLayer(value ?? DEFAULT_SECTION_BACKGROUND_GROUP)
  )

  const [device] = useActiveDevice()
  const ActiveDeviceIcon = BUILDER_DEVICES[device].Icon

  const [scheme] = useBuilderColorSchemeV2()
  const mode = scheme.current

  // Compute fixed layers with no duplicate IDs
  const fixedLayers = useMemo(() => ensureEffectLayer(value ?? DEFAULT_SECTION_BACKGROUND_GROUP), [value])

  // Sync layers when fixedLayers changes
  useEffect(() => {
    setLayers(fixedLayers)
  }, [fixedLayers])

  const layersRef = useRef(layers)
  useEffect(() => {
    layersRef.current = layers
  }, [layers])

  const updateValue = useUpdateCompSettingValue(id)

  // Auto-save if effect layer was added (migration)
  const initialValueRef = useRef(value)
  const hasMigratedRef = useRef(false)
  useEffect(() => {
    if (hasMigratedRef.current) return

    const initialValue = initialValueRef.current
    if (initialValue && !initialValue.some((layer) => layer.type === 'effect')) {
      // Effect layer was added, save it
      hasMigratedRef.current = true
      updateValue(layers as any).catch((error) => {
        console.error('Failed to save effect layer:', error)
      })
    }
  }, [layers, updateValue])

  const debouncedUpdateRef = useRef(
    debounce(async (latestLayers: typeof layers) => {
      try {
        await updateValue(latestLayers as any)
      } catch (error) {
        console.error('Failed to update value:', error)
      }
    }, 50)
  )

  useEffect(() => {
    debouncedUpdateRef.current(layers)
  }, [layers])

  const handleUpdate = async (id: number, newValues: any) => {
    const payload = layers.map((layer) =>
      layer.id === id ? { ...layer, values: { ...layer.values, ...newValues } } : layer
    )
    setLayers(payload)
  }

  const handleDragStart = (e: React.DragEvent, id: { toString: () => any }) => {
    e.dataTransfer.setData('text/plain', id.toString())
  }

  const handleDragOver = (e: { preventDefault: () => void }) => {
    e.preventDefault()
  }

  const handleDrop = async (
    e: { preventDefault: () => void; dataTransfer: { getData: (arg0: string) => string } },
    targetId: number
  ) => {
    e.preventDefault()
    const draggedId = parseInt(e.dataTransfer.getData('text/plain'))
    if (draggedId === targetId) return

    const newLayers = [...layers]
    const draggedIndex = layers.findIndex((layer) => layer.id === draggedId)
    const targetIndex = layers.findIndex((layer) => layer.id === targetId)
    const [draggedLayer] = newLayers.splice(draggedIndex, 1)
    newLayers.splice(targetIndex, 0, draggedLayer)
    setLayers(newLayers)
    debouncedUpdateRef.current(newLayers)
  }

  const renderControls = (layer: SectionBackgroundGroupProps) => {
    switch (layer.type) {
      case 'color':
        return <ColorBackgroundSelect layer={layer} handleUpdate={handleUpdate} settingId={settingId} mode={mode} />
      case 'overlay':
        return <OverlayBackgroundSelect layer={layer} handleUpdate={handleUpdate} />
      case 'effect':
        return <EffectBackgroundSelect layer={layer} handleUpdate={handleUpdate} />
      case 'mask':
        return <MaskBackgroundSelect layer={layer} handleUpdate={handleUpdate} />
      case 'video':
        return (
          <VideoBackgroundSelect
            layer={layer}
            handleUpdate={handleUpdate}
            videoSelectSettings={videoSelectSettings}
            id={id}
            settingId={settingId}
          />
        )
      case 'image':
        return (
          <ImageBackgroundSelect
            layer={layer}
            handleUpdate={handleUpdate}
            imageSelectSettings={imageSelectSettings}
            id={id}
            settingId={settingId}
          />
        )
      case 'pattern':
        return (
          <PatternBackgroundSelect
            layer={layer}
            handleUpdate={handleUpdate}
            colorVariantSettings={colorVariantSettings}
            compSettings={compSettings}
          />
        )
      default:
        break
    }
  }

  return (
    <div className='flex flex-col gap-2 w-full'>
      {layers?.map((layer) => {
        const isVideo = layer.type === 'video'
        const isColor = layer.type === 'color'
        const isMask = layer.type === 'mask'
        const isAnimation = layer.type === 'effect'
        const isDark = mode === 'dark'
        return (
          <div
            key={layer.id}
            className='rounded-sm bg-white dark:bg-gray-700 shadow-xs'
            draggable
            onDragStart={(e) => handleDragStart(e, layer.id)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, layer.id)}
          >
            <button
              type='button'
              className='w-full flex items-center gap-4 py-3 px-4 cursor-pointer bg-gray-50 dark:bg-transparent hover:bg-gray-80 dark:hover:bg-gray-500'
              onClick={(e) => {
                e.stopPropagation()
                setOpenLayerId(openLayerId === layer.id ? null : layer.id)
              }}
            >
              <div className='flex-1 flex items-center gap-2'>
                <Reorder16 />
                <h3 className='font-medium capitalize'>
                  {isVideo
                    ? 'YouTube Video'
                    : isAnimation
                      ? 'Animation'
                      : `${layer.type} ${isColor ? (isDark ? '(Dark)' : '(Light)') : ''}`}
                </h3>
              </div>
              {isColor ? isDark ? <Moon24 className='w-3.5 h-3.5' /> : <Sun24 className='w-3.5 h-3.5' /> : null}
              {isMask && <ActiveDeviceIcon className='w-3.5 h-3.5' />}
              {openLayerId === layer.id ? (
                <ChevronSmallUp className='w-3 h-3' />
              ) : (
                <ChevronSmallDown className='w-3 h-3' />
              )}
            </button>
            {openLayerId === layer.id && (
              <div className='p-4 bg-gray-50 dark:bg-gray-700 border-t dark:border-t-gray-500'>
                {renderControls(layer)}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
